<template>
  <div class="page-section">
    <div :class="containerClass">
      <h2 class="text-center mb-2">My Info</h2>
      <p
        class="text-center mb-4"
        v-if="
          getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS ||
            getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION
        "
      >
        Manage and showcase your career readiness by tracking your progress with these tools.
      </p>
      <p class="text-center mb-4" v-else>
        View and manage your Questions, Skills, Documents, Payments, Resume and Sponsorship Applications on my info.
      </p>
      <div v-if="isCareCampus" class="d-flex justify-content-center mb-4">
        <radio-button-group
          :options="infoTabs"
          :primary-color="brandColor"
          name="my-info-tabs"
          :model-value="tab"
          @update:modelValue="setTab($event)"
        />
      </div>

      <div v-if="!isCareCampus || tab === 'uploads'" class="row mb-5">
        <template v-for="info in myInfo">
          <DashboardCard :key="info.id" :guide="info" color="primary" class="col-md-6 mb-2" />
        </template>
      </div>
      <my-checklists v-else embedded />
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DASHBOARD_TYPES } from '@/common/constants';
import DashboardCard from '@/components/DashboardCard.vue';
import RadioButtonGroup from '@/components/RadioGroup.vue';
import MyChecklists from './MyChecklists.vue';
import { mapActions } from 'vuex';
import {
  ACADEMIC_EXCELLENCE_OPTIONS,
  CAREER_OPPORTUNITIES_OPTIONS,
  CAREER_PREPARATION_OPTIONS,
  getCategoryItems,
} from '@/common/dashboardCategories';


export default {
  extends: Page,
  components: { DashboardCard, RadioButtonGroup, MyChecklists },
  data() {
    return {
      title: 'My Info',
      windowWidth: window.innerWidth,
      DASHBOARD_TYPES,
      tab: 'checklists',
      infoTabs: [
        { text: 'Programs', value: 'checklists' },
        { text: 'Uploads', value: 'uploads' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    ...mapGetters('school', ['getInAppPaymentStatus', 'getPaymentStatus']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Setup Guide', active: true },
      ];
    },
    isMobileScreen() {
      return this.windowWidth <= 600;
    },
    isCareCampus() {
      return this.getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS;
    },
    brandColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color') || '#3b86f5';
    },
    myInfo() {
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        const academicItems = getCategoryItems(
          ACADEMIC_EXCELLENCE_OPTIONS,
          this.getLoggedInUserDashboard,
          this.userSettings
        );

        const careerOpportunitiesItems = getCategoryItems(
          CAREER_OPPORTUNITIES_OPTIONS,
          this.getLoggedInUserDashboard,
          this.userSettings
        );

        const careerPreparationItems = getCategoryItems(
          CAREER_PREPARATION_OPTIONS,
          this.getLoggedInUserDashboard,
          this.userSettings
        );

        // Handle special action for Program Certificate
        const items = [...academicItems, ...careerOpportunitiesItems, ...careerPreparationItems];

        return items.map(item => {
          if (item.id === 'program-certificate') {
            return {
              ...item,
              handleTranscriptDownload: () => {
                this.handleTranscriptDownload();
              },
            };
          }
          return item;
        });
      }

      // TransitionU (CareCampus) dashboard — credential & application profile.
      // Ordered: credentials -> coursework/exam prep -> application materials -> career tools.
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS) {
        return [
          {
            title: 'My Certifications',
            abbr: 'add_certifications',
            btnText: 'Complete',
            description: 'Showcase your certifications to demonstrate your skills and expertise.',
            enable: true,
            icon: 'workspace_premium',
            redirectTo: 'my-certifications',
          },
          {
            title: 'My Licenses',
            abbr: 'add_licenses',
            btnText: 'Complete',
            description: 'Add professional licenses to your profile.',
            enable: true,
            icon: 'badge',
            redirectTo: 'my-licenses',
          },
          {
            title: 'My Degrees',
            abbr: 'add_degrees',
            btnText: 'Complete',
            description: 'Add degrees to demonstrate your academic background.',
            enable: true,
            icon: 'school',
            redirectTo: 'my-degrees',
          },
          {
            title: 'My Courses',
            abbr: 'add_new_courses',
            btnText: 'Complete',
            description: 'Highlight the courses you enrolled to showcase your career growth.',
            enable: true,
            icon: 'local_library',
            redirectTo: 'my-courses',
          },
          {
            title: 'My Prep Courses',
            abbr: 'add_prep_courses',
            btnText: 'Complete',
            description: "Add prep courses you've completed toward your next credential.",
            enable: true,
            icon: 'menu_book',
            redirectTo: 'my-prep-courses',
          },
          {
            title: 'My Admissions Assessments',
            abbr: 'add_admissions_assessments',
            btnText: 'Complete',
            description: 'Add entrance exams like TEAS or HESI to your profile.',
            enable: true,
            icon: 'assignment',
            redirectTo: 'my-admissions-assessments',
          },
          {
            title: 'My Letters of Recommendation',
            abbr: 'add_letters_of_recommendation',
            btnText: 'Complete',
            description: 'Add letters of recommendation to strengthen your applications.',
            enable: true,
            icon: 'mail',
            redirectTo: 'my-letters-of-recommendation',
          },
          {
            title: 'My Activities',
            abbr: 'add_activities',
            btnText: 'Complete',
            description: 'Showcase clubs, leadership, and volunteer activities.',
            enable: true,
            icon: 'emoji_events',
            redirectTo: 'my-activities',
          },
          {
            id: 3,
            title: 'My Resume',
            description: 'View and update your custom resume for employment.',
            abbr: 'resume_build',
            redirectTo: 'std-manage-resume',
            icon: 'description',
            btnText: 'Complete',
          },
          {
            id: 8,
            title: 'My Interviews',
            description: 'Review your submitted interviews to track your progress and prepare for potential recruiters.',
            redirectTo: 'student-submissions-listing',
            icon: 'mic',
            btnText: 'Search',
          },
        ];
      }

      // Legacy flow for non-school dashboards
      return [
        ...(this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL
          ? [
              {
                id: 1,
                title: 'My Uploads',
                description: 'View and manage your uploaded documents and credentials.',
                abbr: 'uploaded_docs',
                redirectTo: 'std-manage-records',
                icon: 'drive_folder_upload',
                btnText: 'Complete',
              },
              {
                id: 2,
                title: 'My Payments',
                description: `Track and organize your payment history.`,
                abbr: 'payments_paid',
                redirectTo: 'std-payment-list',
                icon: 'payment',
                btnText: 'Complete',
              },
              {
                id: 4,
                title: 'My Applications',
                description: 'Monitor the status of your submitted sponsorship applications.',
                redirectTo: 'std-manage-applications',
                icon: 'assignment',
                btnText: 'Search',
              },
            ]
          : []),
        ...(this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL &&
        this.getLoggedInUser?.linked_entity?.ai_skills_enabled === true
          ? [
              {
                id: 11,
                title: 'My Skill Cards',
                description: 'View and manage your skill cards.',
                abbr: 'my_skillmaps',
                redirectTo: 'std-profile-skillcard',
                icon: 'map',
                btnText: 'View',
              },
            ]
          : []),
        {
          id: 3,
          title: 'My Resume',
          description: 'View and update your custom resume for employment.',
          abbr: 'resume_build',
          redirectTo: 'std-manage-resume',
          icon: 'description',
          btnText: 'Complete',
        },
        {
          id: 8,
          title: 'My Interviews',
          description: 'Review your submitted interviews to track your progress and prepare for potential recruiters.',
          redirectTo: 'student-submissions-listing',
          icon: 'mic',
          btnText: 'Search',
        },
        {
          title: 'My Certifications',
          abbr: 'add_certifications',
          btnText: 'Complete',
          description: 'Showcase your certifications to demonstrate your skills and expertise.',
          enable: true,
          icon: 'workspace_premium',
          redirectTo: 'my-certifications',
        },
        ...([DASHBOARD_TYPES.ORGANIZATION, DASHBOARD_TYPES.SCHOOL].includes(this.getLoggedInUserDashboard)
          ? [
              {
                title: 'My Job Offers',
                abbr: 'add_new_offers',
                btnText: 'Complete',
                description: 'Highlight past job offers to showcase your career growth and industry demand.',
                enable: true,
                icon: 'payment',
                redirectTo: 'my-offers',
              },
              {
                title: 'My Research',
                abbr: 'add_new_research',
                btnText: 'Complete',
                description: 'Highlight your research to showcase your expertise and academic involvement.',
                enable: true,
                icon: 'menu_book',
                redirectTo: 'my-research',
              },
            ]
          : []),
        ...(this.getInAppPaymentStatus && this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL
          ? [
              {
                id: 5,
                title: 'My Plans',
                description: `View and manage your upcoming dues`,
                redirectTo: 'manage-plans',
                icon: 'attach_money',
                btnText: 'Find',
              },
            ]
          : []),
        ...(this.getPaymentStatus && this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL
          ? [
              {
                id: 6,
                title: 'My Skills',
                description: `View and manage skills for your career profile.`,
                redirectTo: 'my-skills',
                icon: 'videocam',
                btnText: 'Find',
              },
              {
                id: 7,
                title: 'Discussions',
                description: `Review your discussions and responses for continuous learning.`,
                redirectTo: 'my-questions',
                icon: 'help',
                btnText: 'Find',
              },
              {
                id: 9,
                title: 'Schedule and Attendance',
                description: `See your class schedule and attendance records.`,
                redirectTo: 'student-attendance',
                icon: 'calendar_today',
                btnText: 'Find',
              },
              {
                id: 12,
                title: 'Program Certificate',
                description: 'Download your program certificate for the registered course.',
                icon: 'receipt',
                btnText: 'Program Certificate',
                btnIcon: 'file_download',
                handleTranscriptDownload: () => {
                  this.handleTranscriptDownload();
                },
              },
            ]
          : []),
      ];
    },
    userSettings() {
      return {
        requiresPayment: this.getPaymentStatus,
        requiresInAppPayment: this.getInAppPaymentStatus,
        aiSkillsEnabled: this.getLoggedInUser?.linked_entity?.ai_skills_enabled || false,
      };
    },
  },

  methods: {
    ...mapActions('student', ['getStudTranscript']),
    ...mapActions('fileDownload', ['downloadFile']),
    get,
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    tabFromRoute() {
      const requested = String(this.$route.query.tab || '');
      return this.infoTabs.some(t => t.value === requested) ? requested : 'checklists';
    },
    setTab(value) {
      this.tab = value;
      if ((this.$route.query.tab || 'checklists') !== value) {
        this.$router.replace({ query: { ...this.$route.query, tab: value === 'checklists' ? undefined : value } });
      }
    },
    async handleTranscriptDownload() {
      try {
        const user = this.getLoggedInUser;
        const resp = await this.getStudTranscript({ id: user.id });
        const h = this.$createElement;
        const url = resp.data.data.url;
        if (url) {
          await this.downloadFile({ fileUrl: resp.data.data.url });

          this.makeToast({
            variant: 'success',
            msg: h('span', [h('i', { class: 'fas  fa-download mr-2' }), 'File Downloaded']),
            toaster: 'b-toaster-bottom-left',
          });
        } else {
          this.makeToast({
            variant: 'danger',
            msg: h('span', ['Transcript Not Found']),
            toaster: 'b-toaster-top-right',
          });
        }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },
  watch: {
    '$route.query.tab'() {
      this.tab = this.tabFromRoute();
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.tab = this.tabFromRoute();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
